import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"
import { Users, Search, ChevronRight, ChevronLeft, List, Loader2 } from "lucide-react"
import { useState, useEffect } from "react"
import { Textarea } from "../ui/textarea"
import { Badge } from "../ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { ScrollArea } from "@/components/ui/scroll-area"
import { ContactsPagination } from "../contactsPage/ContactsPagination"
import { useDebounce } from "@/hooks/useDebounce"
import { apiFetch } from "@/lib/api/apiFetch"
import { useListHook } from "@/hooks/useListHook"

export function AddToList({ contactLists = [], setSelectedContacts }: any) {

    /**
       * Custom hook that handles:
       * - Fetching recipient lists
       * - Pagination
       * - Archiving logic
       * - Navigation to list details
       */
    const {
        fetchRecipientLists,
        lists,
        pagination,
        isLoading
    } = useListHook()


    const [isOpen, setIsOpen] = useState(false)
    const [step, setStep] = useState<1 | 2>(1)
    const [searchTerm, setSearchTerm] = useState("")
    const [selectedList, setSelectedList] = useState<any>(null)
    const initialFormData = {
        name: "",
        description: "",
        remarks: "",
        contactIds: [],
    }
    const [formData, setFormData] = useState(initialFormData)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { toast } = useToast()

    const debouncedSearch = useDebounce(searchTerm, 500)

    useEffect(() => {
        if (isOpen) {
            fetchRecipientLists(1, debouncedSearch)
        }
    }, [debouncedSearch, isOpen])

    const handleListSelect = (list: any) => {
        setSelectedList(list)
        setFormData({
            name: list.name || "",
            description: list.description || "",
            remarks: list.remarks || "",
            contactIds: list.contacts?.map((c: any) => c._id) || [],
        })
    }

    const isListSelected = (listId: string) => {
        return selectedList?._id === listId
    }

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            toast({ title: "Validation Error", description: "List name is required", variant: "destructive" })
            return
        }
        if (formData.contactIds.length === 0) {
            toast({ title: "Validation Error", description: "Please select at least one contact", variant: "destructive" })
            return
        }

        setIsSubmitting(true)
        try {
            // New contact IDs from props (from selectedContacts in Header)
            const newContactIds = contactLists.map((c: any) => (typeof c === 'string' ? c : c._id || c)).filter(Boolean)
            // console.log("newContactIds", newContactIds)
            // Existing contact IDs from the selected list
            const existingContactIds = selectedList.contacts?.map((c: any) => (typeof c === 'string' ? c : c._id || c)).filter(Boolean) || []
            // console.log("existingContactIds", existingContactIds)
            // Merge and de-duplicate
            const combinedContactIds = Array.from(new Set([...existingContactIds, ...newContactIds]))
            // console.log("combinedContactIds", combinedContactIds)
            await apiFetch(`/api/recipient-lists/${selectedList._id}`, {
                method: "PUT",
                data: {
                    name: formData.name.trim(),
                    description: formData.description.trim(),
                    remarks: formData.remarks.trim(),
                    contacts: combinedContactIds,
                },
            })

            toast({ title: "Success", description: `Updated list "${formData.name}" with ${newContactIds.length} new contacts (Total: ${combinedContactIds.length})` })
            setIsOpen(false)
            handleOpenChange(false)
            setSelectedContacts([])
        } catch (error: any) {
            toast({ title: "Error", description: error.message || "Failed to update recipient list", variant: "destructive" })
        } finally {
            fetchRecipientLists()
            setIsSubmitting(false)
        }
    }

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open)
        if (!open) {
            setStep(1)
            setSelectedList(null)
            setFormData(initialFormData)
            setSearchTerm("")
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => e.stopPropagation()}
                    title="Edit list"
                    disabled={contactLists.length === 0}
                    className="h-10 px-4 font-semibold border-muted-foreground/20"
                >
                    <List className="h-4 w-4 mr-2" />
                    Add to list{` (${contactLists.length})`}
                </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-[60%] max-h-[90vh] flex flex-col">

                {/* Header */}
                <DialogHeader>
                    <div className="flex items-center justify-between pr-6">
                        <div>
                            <DialogTitle className="mb-2">Add to List</DialogTitle>
                            <DialogDescription>
                                {step === 1
                                    ? "Step 1 of 2 — Select List to add contacts to."
                                    : "Step 2 of 2 — Update list name, description and remarks."}
                            </DialogDescription>
                        </div>
                        {/* Step indicator */}
                        <div className="flex items-center gap-2">
                            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold border-2 transition-colors ${step === 1 ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-400 border-gray-300"}`}>
                                1
                            </div>
                            <div className="w-6 h-px bg-gray-300" />
                            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold border-2 transition-colors ${step === 2 ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-400 border-gray-300"}`}>
                                2
                            </div>
                        </div>
                    </div>
                </DialogHeader>

                {/* Body */}
                <div className="flex-1 overflow-hidden flex flex-col gap-4 py-2 px-1">

                    {/* ── STEP 1: list table ── */}
                    {step === 1 && (
                        <div className="flex flex-col gap-3 flex-1 min-h-0">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">
                                    Selected Contacts{" "}
                                    <span className="ml-1 text-xs text-gray-500 font-normal">
                                        ({contactLists.length} selected)
                                    </span>
                                </Label>
                                <div className="relative w-60">
                                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                                    <Input
                                        placeholder="Search lists..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-8 h-9 text-sm"
                                    />
                                </div>
                            </div>

                            <div className="border rounded-md flex-1 overflow-hidden">
                                <ScrollArea className="h-[300px]">
                                    <Table>
                                        <TableHeader className="sticky top-0 bg-secondary z-10">
                                            <TableRow>
                                                <TableHead className="w-[50px]" />
                                                <TableHead>Name</TableHead>
                                                <TableHead>No. of Contacts</TableHead>
                                                <TableHead>Status</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {isLoading ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} className="text-center py-8">
                                                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                                                    </TableCell>
                                                </TableRow>
                                            ) : lists.length > 0 ? (
                                                lists.map((list: any) => (
                                                    <TableRow
                                                        key={list._id}
                                                        className={`cursor-pointer ${isListSelected(list._id) ? "bg-muted/50" : ""}`}
                                                        onClick={() => handleListSelect(list)}
                                                    >
                                                        <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                                                            <Checkbox
                                                                checked={isListSelected(list._id)}
                                                                onCheckedChange={() => handleListSelect(list)}
                                                            />
                                                        </TableCell>
                                                        <TableCell className="py-2 font-medium">{list.name || "N/A"}</TableCell>
                                                        <TableCell className="py-2">{list.contactCount || 0}</TableCell>
                                                        <TableCell className="py-2">
                                                            <Badge variant="outline" className="text-xs">
                                                                {list.isActive ? "Active" : "Inactive"}
                                                            </Badge>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={4} className="h-24 text-center text-sm text-gray-400">
                                                        No list found.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </ScrollArea>
                            </div>

                            <ContactsPagination
                                pagination={pagination}
                                onPageChange={(page) => fetchRecipientLists(page, debouncedSearch)}
                                title="Lists"
                            />
                        </div>
                    )}

                    {/* ── STEP 2: List details form ── */}
                    {step === 2 && (
                        <div className="flex flex-col gap-4 py-2">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">List Name <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Enter list name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="description">Description</Label>
                                    <Input
                                        id="description"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Brief description"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="remarks">Remarks</Label>
                                <Textarea
                                    id="remarks"
                                    value={formData.remarks}
                                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                    placeholder="Add notes"
                                    rows={3}
                                />
                            </div>

                            {/* Summary chip */}

                        </div>
                    )}
                </div>

                {/* Footer */}
                <DialogFooter className="gap-2">
                    {step === 1 ? (
                        <>
                            <DialogClose asChild>
                                <Button variant="outline" type="button">Cancel</Button>
                            </DialogClose>
                            <Button
                                type="button"
                                onClick={() => setStep(2)}
                                disabled={!selectedList}
                                className="gap-1.5"
                            >
                                Next
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="outline"
                                type="button"
                                onClick={() => setStep(1)}
                                className="gap-1.5"
                            >
                                <ChevronLeft className="h-4 w-4" />
                                Back
                            </Button>
                            <Button
                                type="button"
                                onClick={handleSubmit}
                                disabled={isSubmitting || !formData.name.trim()}
                            >
                                {isSubmitting ? "Saving..." : "Save Changes"}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}